<script lang="ts" setup>
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { faBullhorn, faChartSimple, faCheck, faLock, faSliders, faUserShield, faXmark } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useUserStore, type CookieConsent } from '@/plugins/pinia/userStore';
import { useI18n } from 'vue-i18n';

const i18n = useI18n();
const { t } = i18n;

const user = useUserStore();

const preferencesOpen = ref(false);
const reopened = ref(false);
const preferences = ref<CookieConsent>({ analytics: false, marketing: false });

const bannerVisible = computed(() => !preferencesOpen.value && (reopened.value || !user.hasCookieConsent));
const badgeVisible = computed(() => !bannerVisible.value && !preferencesOpen.value);

const toggleableCategories: { key: keyof CookieConsent; icon: IconDefinition }[] = [
  { key: 'analytics', icon: faChartSimple },
  { key: 'marketing', icon: faBullhorn },
];

function openPreferences() {
  preferences.value = {
    analytics: user.cookieConsent?.analytics ?? false,
    marketing: user.cookieConsent?.marketing ?? false,
  };
  preferencesOpen.value = true;
}

function closePreferences() {
  preferencesOpen.value = false;
}

function acceptAll() {
  user.acceptCookies();
  preferencesOpen.value = false;
  reopened.value = false;
}

function rejectAll() {
  user.rejectCookies();
  preferencesOpen.value = false;
  reopened.value = false;
}

function savePreferences() {
  user.setCookieConsent(preferences.value);
  preferencesOpen.value = false;
  reopened.value = false;
}

function reopenBanner() {
  reopened.value = true;
}
</script>

<template>
  <transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-full"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-full"
  >
    <div v-if="bannerVisible" :aria-label="t('components.cookie-consent.title')" class="border-ph-green-600 fixed inset-x-0 bottom-0 z-100 border-t-3 bg-white shadow-2xl" role="region">
      <div class="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:gap-6 sm:px-6">
        <FontAwesomeIcon :icon="faUserShield" class="text-ph-green-700 hidden size-8 shrink-0 sm:block" />
        <p :class="i18n.locale.value === 'fr-CA' ? 'tracking-tighter' : 'tracking-tight'" class="grow text-sm text-gray-600">
          {{ t('components.cookie-consent.disclaimer') }}
          <RouterLink :to="{ name: 'privacy' }" class="text-ph-green-700 hover:text-ph-green-600 text-nowrap hover:underline">{{ t('components.cookie-consent.learn-more') }}</RouterLink>
        </p>
        <div class="flex shrink-0 gap-3">
          <button
            class="bg-ph-green-700 hover:bg-ph-green-600 focus-visible:outline-ph-green-700 flex-1 cursor-pointer rounded-lg px-4 py-2 font-semibold text-nowrap text-white transition-all focus-visible:outline-2 focus-visible:outline-offset-2 sm:flex-none sm:px-6"
            @click="acceptAll"
          >
            <FontAwesomeIcon :icon="faCheck" class="mr-1" />
            {{ t('components.cookie-consent.accept') }}
          </button>
          <button
            class="focus-visible:outline-ph-green-700 flex-1 cursor-pointer rounded-lg bg-gray-600 px-4 py-2 font-semibold text-nowrap text-white transition-all hover:bg-gray-500 focus-visible:outline-2 focus-visible:outline-offset-2 sm:flex-none sm:px-6"
            @click="openPreferences"
          >
            <FontAwesomeIcon :icon="faSliders" class="mr-1" />
            {{ t('components.cookie-consent.customize') }}
          </button>
        </div>
      </div>
    </div>
  </transition>

  <transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-4 translate-x-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4 translate-x-4"
  >
    <div v-if="badgeVisible" class="border-ph-green-600 fixed right-2 bottom-2 z-100 rounded-2xl border-3 bg-white shadow-2xl">
      <button :aria-label="t('components.cookie-consent.manage')" :title="t('components.cookie-consent.manage')" class="flex cursor-pointer rounded-lg p-2" @click="reopenBanner">
        <FontAwesomeIcon :icon="faUserShield" class="text-ph-green-700 size-7" />
      </button>
    </div>
  </transition>

  <TransitionRoot :show="preferencesOpen" as="template">
    <Dialog class="relative z-101" @close="closePreferences">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6">
              <button :aria-label="t('components.cookie-consent.preferences.close')" class="absolute top-4 right-4 cursor-pointer rounded-lg p-2 text-gray-400 transition-colors hover:text-gray-600" type="button" @click="closePreferences">
                <FontAwesomeIcon :icon="faXmark" />
              </button>

              <DialogTitle as="h2" class="pr-8 text-base font-semibold text-gray-900">
                {{ t('components.cookie-consent.preferences.title') }}
              </DialogTitle>
              <p class="mt-2 text-sm text-gray-500">
                {{ t('components.cookie-consent.preferences.intro') }}
                <RouterLink :to="{ name: 'privacy' }" class="text-ph-green-700 hover:text-ph-green-600 text-nowrap hover:underline">{{ t('components.cookie-consent.learn-more') }}</RouterLink>
              </p>

              <ul class="mt-6 divide-y divide-gray-200 border-y border-gray-200">
                <li class="flex items-start gap-4 py-4">
                  <FontAwesomeIcon :icon="faLock" class="text-ph-green-700 mt-1 size-4 shrink-0" />
                  <div class="grow">
                    <p class="text-sm font-semibold text-gray-900">{{ t('components.cookie-consent.preferences.necessary.name') }}</p>
                    <p class="mt-1 text-xs text-gray-500">{{ t('components.cookie-consent.preferences.necessary.description') }}</p>
                  </div>
                  <span class="bg-ph-green-50 text-ph-green-800 shrink-0 rounded-full px-3 py-1 text-xs font-semibold">{{ t('components.cookie-consent.preferences.always-active') }}</span>
                </li>
                <li v-for="category in toggleableCategories" :key="category.key" class="flex items-start gap-4 py-4">
                  <FontAwesomeIcon :icon="category.icon" class="text-ph-green-700 mt-1 size-4 shrink-0" />
                  <div class="grow">
                    <p :id="`cookie-consent-${category.key}`" class="text-sm font-semibold text-gray-900">{{ t(`components.cookie-consent.preferences.${category.key}.name`) }}</p>
                    <p class="mt-1 text-xs text-gray-500">{{ t(`components.cookie-consent.preferences.${category.key}.description`) }}</p>
                  </div>
                  <button
                    :aria-checked="preferences[category.key]"
                    :aria-labelledby="`cookie-consent-${category.key}`"
                    :class="preferences[category.key] ? 'bg-ph-green-600' : 'bg-gray-300'"
                    class="focus-visible:outline-ph-green-700 relative mt-1 h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
                    role="switch"
                    type="button"
                    @click="preferences[category.key] = !preferences[category.key]"
                  >
                    <span :class="preferences[category.key] ? 'translate-x-5' : 'translate-x-0'" class="pointer-events-none ml-0.5 block size-5 rounded-full bg-white shadow transition-transform" />
                  </button>
                </li>
              </ul>

              <div class="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  class="focus-visible:outline-ph-green-700 cursor-pointer rounded-lg bg-gray-600 px-4 py-2 text-sm font-semibold text-nowrap text-white transition-all hover:bg-gray-500 focus-visible:outline-2 focus-visible:outline-offset-2"
                  type="button"
                  @click="rejectAll"
                >
                  <FontAwesomeIcon :icon="faXmark" class="mr-1" />
                  {{ t('components.cookie-consent.preferences.reject-all') }}
                </button>
                <button
                  class="focus-visible:outline-ph-green-700 border-ph-green-700 text-ph-green-700 hover:bg-ph-green-50 cursor-pointer rounded-lg border-2 px-4 py-2 text-sm font-semibold text-nowrap transition-all focus-visible:outline-2 focus-visible:outline-offset-2"
                  type="button"
                  @click="savePreferences"
                >
                  {{ t('components.cookie-consent.preferences.save') }}
                </button>
                <button
                  class="bg-ph-green-700 hover:bg-ph-green-600 focus-visible:outline-ph-green-700 cursor-pointer rounded-lg px-4 py-2 text-sm font-semibold text-nowrap text-white transition-all focus-visible:outline-2 focus-visible:outline-offset-2"
                  type="button"
                  @click="acceptAll"
                >
                  <FontAwesomeIcon :icon="faCheck" class="mr-1" />
                  {{ t('components.cookie-consent.preferences.accept-all') }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style lang="css" scoped></style>
